import { GLOBAL_VOLUME } from './config';
import { SOUND_EFFECTS, WARM_SOUND_EFFECTS } from './soundEffects';

const VOLUME = Math.max(0, Math.min(GLOBAL_VOLUME, 1));
const MAX_DECODED_BYTES = 32 * 1024 * 1024;
const MAX_START_DELAY_MS = 3000;
const INPUT_EVENTS = [
  'pointerdown',
  'pointerup',
  'touchend',
  'click',
  'keydown',
];
const encodedSounds = new Map();
const decodedSounds = new Map();
const decodingSounds = new Map();
const activeSounds = new Map();
const fallbackPools = new Map();
let decodedBytes = 0;
let audioContext = null;
let masterGain = null;
let primedContext = null;
let recoveringContext = null;
let restoreAudioOnForeground = false;
let preloadStarted = false;

const reportError = (src, error) => {
  if (process.env.NODE_ENV !== 'production')
    console.warn(`Unable to play sound ${src}:`, error);
};

const releaseSound = (request) => {
  if (request.released) return;
  request.released = true;
  clearTimeout(request.expiry);
  if (request.source) {
    request.source.onended = null;
    if (request.started) request.source.stop();
    request.source.disconnect();
  }
  if (request.element) {
    request.element.removeEventListener('ended', request.onEnded);
    request.element.removeEventListener('error', request.onError);
    request.element.removeEventListener('playing', request.onPlaying);
    request.element.pause();
  }
  const requests = activeSounds.get(request.src);
  requests?.delete(request);
  if (!requests?.size) activeSounds.delete(request.src);
  const onComplete = request.onComplete;
  request.onComplete = null;
  onComplete?.();
};

const stopAllSounds = () => {
  activeSounds.forEach((requests) => requests.forEach(releaseSound));
};

const flushSounds = () => {
  if (audioContext?.state !== 'running' || document.hidden) return;
  activeSounds.forEach((requests) => {
    requests.forEach((request) => {
      if (request.released || request.started || !request.buffer) return;
      if (Date.now() - request.createdAt > MAX_START_DELAY_MS) {
        releaseSound(request);
        return;
      }
      try {
        const source = audioContext.createBufferSource();
        source.buffer = request.buffer;
        source.connect(masterGain);
        source.onended = () => releaseSound(request);
        request.source = source;
        source.start(0);
        request.started = true;
        clearTimeout(request.expiry);
      } catch (error) {
        releaseSound(request);
        reportError(request.src, error);
      }
    });
  });
};

const getAudioContext = () => {
  if (audioContext && audioContext.state !== 'closed') return audioContext;
  if (typeof window === 'undefined') return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext || typeof fetch !== 'function') return null;
  try {
    audioContext = new AudioContext();
    masterGain = audioContext.createGain();
    masterGain.gain.value = VOLUME;
    masterGain.connect(audioContext.destination);
    const context = audioContext;
    context.onstatechange = () => {
      if (context !== audioContext) return;
      if (context.state === 'running') flushSounds();
      else if (['interrupted', 'closed'].includes(context.state))
        stopAllSounds();
    };
    return audioContext;
  } catch (error) {
    audioContext = null;
    reportError('audio engine', error);
    return null;
  }
};

const resumeAudio = () => {
  const context = audioContext;
  if (!context || context.state === 'closed' || document.hidden) return;
  if (context.state === 'running') {
    flushSounds();
    return;
  }
  if (context.state === 'interrupted') {
    if (recoveringContext === context) return;
    recoveringContext = context;
    // Safari can remain interrupted until it has explicitly been suspended.
    // This context was already unlocked before the system interruption.
    context
      .suspend()
      .then(() => {
        if (audioContext === context && !document.hidden)
          return context.resume();
        return undefined;
      })
      .then(flushSounds)
      .catch(() => {})
      .finally(() => {
        if (recoveringContext === context) recoveringContext = null;
      });
    return;
  }
  // Invoke resume synchronously in the input handler. Awaiting a fetch or
  // decode first loses the user activation that iPhone Safari requires.
  context
    .resume()
    .then(flushSounds)
    .catch(() => {
      // A locked or interrupted session can be retried by the next real input.
    });
};

const setAudioSessionType = (type) => {
  try {
    if (navigator.audioSession && navigator.audioSession.type !== type)
      navigator.audioSession.type = type;
  } catch {
    // Audio Session is optional; the shared context also works without it.
  }
};

const unlockAudio = () => {
  // A native interruption can leave suspend/resume pending indefinitely.
  // Retire that graph and unlock its replacement within this real gesture.
  if (audioContext && ['interrupted', 'closed'].includes(audioContext.state)) {
    onBackground();
    restoreAudioOnForeground = false;
  }
  // Match the media playback channel used by the previous HTML audio players
  // on Safari versions that expose Audio Session, including Silent mode.
  setAudioSessionType('playback');
  const context = getAudioContext();
  if (!context) return;
  resumeAudio();
  if (primedContext === context) return;
  try {
    const silent = context.createBufferSource();
    silent.buffer = context.createBuffer(1, 1, context.sampleRate);
    silent.connect(masterGain);
    silent.onended = () => silent.disconnect();
    silent.start(0);
    primedContext = context;
  } catch (error) {
    reportError('audio unlock', error);
  }
};

const loadEncodedSound = (src) => {
  if (encodedSounds.has(src)) return encodedSounds.get(src);
  const loading = fetch(src)
    .then((response) => {
      if (!response.ok)
        throw new Error(`Sound request failed: ${response.status}`);
      return response.arrayBuffer();
    })
    .catch((error) => {
      encodedSounds.delete(src);
      throw error;
    });
  encodedSounds.set(src, loading);
  return loading;
};

const loadDecodedSound = (src, context) => {
  if (decodedSounds.has(src)) {
    const buffer = decodedSounds.get(src);
    // Refresh access order so frequently used effects stay decoded on phones.
    decodedSounds.delete(src);
    decodedSounds.set(src, buffer);
    return Promise.resolve(buffer);
  }
  if (decodingSounds.has(src)) return decodingSounds.get(src);
  const loading = loadEncodedSound(src)
    .then(
      (data) =>
        // Safari's decoder can detach its input. Retain the compressed original
        // so an evicted effect or a recreated context can decode it again.
        new Promise((resolve, reject) => {
          const decoding = context.decodeAudioData(
            data.slice(0),
            resolve,
            reject
          );
          decoding?.then(resolve, reject);
        })
    )
    .then((buffer) => {
      if (context !== audioContext) return buffer;
      decodedSounds.set(src, buffer);
      decodedBytes += buffer.length * buffer.numberOfChannels * 4;
      while (decodedBytes > MAX_DECODED_BYTES && decodedSounds.size > 1) {
        const oldestSrc = decodedSounds.keys().next().value;
        const oldest = decodedSounds.get(oldestSrc);
        decodedSounds.delete(oldestSrc);
        decodedBytes -= oldest.length * oldest.numberOfChannels * 4;
      }
      return buffer;
    })
    .catch((error) => {
      // A partial download must not poison every later attempt at this clip.
      if (context === audioContext) encodedSounds.delete(src);
      throw error;
    })
    .finally(() => {
      if (decodingSounds.get(src) === loading) decodingSounds.delete(src);
    });
  decodingSounds.set(src, loading);
  return loading;
};

const playFallback = (request) => {
  try {
    const pool = fallbackPools.get(request.src) || [];
    const busy = activeSounds.get(request.src);
    let sound = pool.find(
      (candidate) => ![...busy].some((other) => other.element === candidate)
    );
    if (!sound) {
      sound = new Audio(request.src);
      sound.preload = 'auto';
      pool.push(sound);
      fallbackPools.set(request.src, pool);
    }
    request.element = sound;
    request.onEnded = () => releaseSound(request);
    request.onError = () => {
      releaseSound(request);
      reportError(request.src, sound.error);
    };
    request.onPlaying = () => {
      request.started = true;
      clearTimeout(request.expiry);
    };
    sound.volume = VOLUME;
    if (sound.currentTime > 0) sound.currentTime = 0;
    sound.addEventListener('ended', request.onEnded);
    sound.addEventListener('error', request.onError);
    sound.addEventListener('playing', request.onPlaying);
    sound.play()?.catch((error) => {
      releaseSound(request);
      reportError(request.src, error);
    });
  } catch (error) {
    releaseSound(request);
    reportError(request.src, error);
  }
};

export const playSound = (src, { preventOverlap = false, onComplete } = {}) => {
  if (typeof document === 'undefined' || document.hidden) {
    onComplete?.();
    return;
  }
  const requests = activeSounds.get(src) || new Set();
  if (preventOverlap && requests.size) {
    const activeRequest = requests.values().next().value;
    if (activeRequest && onComplete) {
      const previousOnComplete = activeRequest.onComplete;
      activeRequest.onComplete = () => {
        previousOnComplete?.();
        onComplete();
      };
    } else {
      onComplete?.();
    }
    return;
  }
  const request = {
    src,
    createdAt: Date.now(),
    released: false,
    started: false,
    onComplete,
  };
  requests.add(request);
  activeSounds.set(src, requests);
  // A blocked play promise must not suppress this effect forever, and old
  // animation cues must not all fire together after the next tap.
  request.expiry = setTimeout(() => releaseSound(request), MAX_START_DELAY_MS);
  const context = getAudioContext();
  if (!context) {
    playFallback(request);
    return () => releaseSound(request);
  }
  resumeAudio();
  loadDecodedSound(src, context)
    .then((buffer) => {
      if (request.released) return;
      request.buffer = buffer;
      flushSounds();
    })
    .catch((error) => {
      releaseSound(request);
      reportError(src, error);
    });
  // Cinematics can cancel their own cues on skip, pause, or unmount.
  return () => releaseSound(request);
};

const onForeground = () => {
  if (document.hidden) return;
  if (restoreAudioOnForeground) {
    restoreAudioOnForeground = false;
    setAudioSessionType('playback');
    getAudioContext();
  }
  resumeAudio();
};

const onBackground = () => {
  stopAllSounds();
  const context = audioContext;
  if (!context) return;
  // Safari can report "running" after unlock while its output is dead, and
  // suspend/resume promises can hang. Retire the graph without waiting for it;
  // keep the cached AudioBuffers for a fresh context on return.
  restoreAudioOnForeground = true;
  audioContext = null;
  decodingSounds.clear();
  primedContext = null;
  recoveringContext = null;
  context.onstatechange = null;
  masterGain.disconnect();
  masterGain = null;
  // Reasserting an unchanged "playback" type can leave Safari's native session
  // stale. Release it now, then restore playback on foreground or the next tap.
  setAudioSessionType('ambient');
  try {
    if (context.state !== 'closed') context.close().catch(() => {});
  } catch {
    // Closing an interrupted context must not prevent the next one starting.
  }
};

const onVisibilityChange = () => {
  if (document.hidden) onBackground();
  else onForeground();
};

export const initializeSounds = () => {
  INPUT_EVENTS.forEach((type) =>
    document.addEventListener(type, unlockAudio, {
      capture: true,
      passive: true,
    })
  );
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onForeground);
  window.addEventListener('focus', onForeground);
  window.addEventListener('pagehide', onBackground);
  return () => {
    INPUT_EVENTS.forEach((type) =>
      document.removeEventListener(type, unlockAudio, true)
    );
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onForeground);
    window.removeEventListener('focus', onForeground);
    window.removeEventListener('pagehide', onBackground);
    onBackground();
  };
};

export const preloadSounds = () => {
  if (preloadStarted) return;
  preloadStarted = true;
  const context = getAudioContext();
  if (!context) return;
  WARM_SOUND_EFFECTS.forEach((src) => {
    loadDecodedSound(src, context).catch((error) => reportError(src, error));
  });
  // Fetch the remaining compressed clips with limited concurrency. Decode on
  // demand to keep the 3D game's audio memory bounded on mobile devices.
  const remaining = SOUND_EFFECTS.filter(
    (src) => !WARM_SOUND_EFFECTS.includes(src)
  );
  const loadNext = async () => {
    while (remaining.length) {
      const src = remaining.shift();
      try {
        await loadEncodedSound(src);
      } catch (error) {
        reportError(src, error);
      }
    }
  };
  for (let worker = 0; worker < 3; worker++) loadNext();
};
